import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { supabase } from '../lib/supabase';
import { CheckCircle, Loader, AlertCircle, CreditCard } from 'lucide-react';

const BillingCompletionHandler = ({ children }) => {
  const { user } = useAuth();
  const { tenants, currentTenant, loading: tenantsLoading, refetchTenants } = useTenant();
  const [billingSetup, setBillingSetup] = useState({
    isProcessing: false,
    isComplete: false,
    error: null,
    success: false
  });

  useEffect(() => {
    // Only run if user is authenticated and tenants are loaded
    if (user && !tenantsLoading && tenants.length > 0 && !billingSetup.isProcessing && !billingSetup.isComplete) {
      checkAndProcessPendingBilling();
    }
  }, [user, tenantsLoading, tenants, billingSetup.isProcessing, billingSetup.isComplete]);

  const checkAndProcessPendingBilling = async () => {
    try {
      const pendingBillingData = localStorage.getItem('pendingBillingSetup');
      
      if (!pendingBillingData) {
        setBillingSetup(prev => ({ ...prev, isComplete: true }));
        return;
      }

      const billingData = JSON.parse(pendingBillingData);
      console.log('Found pending billing data:', billingData);
      
      // Verify this billing data matches the current user (with fallback)
      if (billingData.userEmail && billingData.userEmail !== user.email) {
        console.log('Billing data email mismatch:', billingData.userEmail, 'vs', user.email);
        console.log('Clearing mismatched billing data');
        localStorage.removeItem('pendingBillingSetup');
        setBillingSetup(prev => ({ ...prev, isComplete: true }));
        return;
      }
      
      // If no userEmail in billing data, it might be from an old version - still try to process it
      if (!billingData.userEmail) {
        console.log('No userEmail in billing data, assuming it matches current user');
        billingData.userEmail = user.email; // Add the current user's email
      }

      // Start processing
      setBillingSetup(prev => ({ ...prev, isProcessing: true }));

      // Find the tenant that matches the billing data
      const targetTenant = tenants.find(t => 
        t.name.toLowerCase() === billingData.tenantName.toLowerCase()
      );

      if (!targetTenant) {
        throw new Error('Could not find matching organization for billing setup');
      }

      // Check if billing is already set up for this tenant
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('stripe_customer_id, plan')
        .eq('id', targetTenant.id)
        .single();

      if (tenantError) throw tenantError;

      if (tenantData.stripe_customer_id) {
        console.log('Billing already set up for this tenant');
        localStorage.removeItem('pendingBillingSetup');
        setBillingSetup(prev => ({ 
          ...prev, 
          isProcessing: false, 
          isComplete: true, 
          success: true 
        }));
        return;
      }

      // Process the billing setup
      console.log('Setting up billing for tenant:', targetTenant.id);
      console.log('Billing data to send:', {
        action: 'setup_billing_with_payment',
        data: {
          tenant_id: targetTenant.id,
          user_id: user.id,
          email: user.email,
          tenant_name: targetTenant.name,
          selected_plan: billingData.selectedPlan,
          card_data: billingData.cardData,
          billing_address: billingData.billingAddress
        }
      });
      
      const response = await supabase.functions.invoke('stripe-billing', {
        body: {
          action: 'setup_billing_with_payment',
          data: {
            tenant_id: targetTenant.id,
            user_id: user.id,
            email: user.email,
            tenant_name: targetTenant.name,
            selected_plan: billingData.selectedPlan,
            payment_method_id: billingData.payment_method_id, // ✅ Secure token only
            billing_address: billingData.billingAddress
          }
        }
      });
      
      console.log('Raw response from edge function:', response);

      if (response.error) {
        throw new Error(response.error.message || 'Failed to setup billing');
      }

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Billing setup failed');
      }

      console.log('Billing setup successful:', response.data);

      // Clear the stored billing data
      localStorage.removeItem('pendingBillingSetup');

      // Refresh tenants to get updated billing information
      console.log('Refreshing tenant data to show updated billing info...');
      await refetchTenants();

      // Update local state
      setBillingSetup(prev => ({ 
        ...prev, 
        isProcessing: false, 
        isComplete: true, 
        success: true 
      }));

      // Show success message briefly
      setTimeout(() => {
        setBillingSetup(prev => ({ ...prev, success: false }));
      }, 5000);

    } catch (error) {
      console.error('Billing setup error:', error);
      setBillingSetup(prev => ({ 
        ...prev, 
        isProcessing: false, 
        isComplete: true, 
        error: error.message 
      }));

      // Clear the stored billing data on error too
      localStorage.removeItem('pendingBillingSetup');
    }
  };

  const retryBillingSetup = () => {
    setBillingSetup({
      isProcessing: false,
      isComplete: false,
      error: null,
      success: false
    });
  };

  // Show loading screen while processing billing
  if (billingSetup.isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-xl border border-slate-200">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Setting Up Your Subscription</h2>
            <p className="text-slate-600 mb-8">
              We're processing your payment information and setting up your organization. This will just take a moment.
            </p>
            
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Loader className="w-6 h-6 text-blue-600 animate-spin" />
              <span className="text-slate-700 font-medium">Processing payment...</span>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> Please don't close this window while we set up your account.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show success message
  if (billingSetup.success) {
    return (
      <div className="fixed top-4 right-4 z-50">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg max-w-sm">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <h3 className="font-medium text-green-900">Billing Setup Complete!</h3>
              <p className="text-green-700 text-sm">Your subscription is now active.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error message
  if (billingSetup.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-xl border border-red-200">
          <div className="text-center">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Billing Setup Failed</h2>
            <p className="text-slate-600 mb-6">
              We encountered an issue setting up your subscription. Don't worry, your account is still active.
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">{billingSetup.error}</p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={retryBillingSetup}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => setBillingSetup(prev => ({ ...prev, error: null, isComplete: true }))}
                className="w-full border border-slate-300 text-slate-700 py-3 px-4 rounded-lg font-medium hover:bg-slate-50 transition-colors"
              >
                Continue Without Billing
              </button>
            </div>
            
            <p className="text-slate-500 text-sm mt-4">
              You can set up billing later in your organization settings.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal app rendering
  return children;
};

export default BillingCompletionHandler;